interface ContributionLimitEntry {
  year: number;
  rothIra: number;
  k401: number;
}

const CONTRIBUTION_LIMITS: ContributionLimitEntry[] = [
  { year: 2019, rothIra: 6000, k401: 19000 },
  { year: 2020, rothIra: 6000, k401: 19500 },
  { year: 2021, rothIra: 6000, k401: 19500 },
  { year: 2022, rothIra: 6000, k401: 20500 },
  { year: 2023, rothIra: 6500, k401: 22500 },
  { year: 2024, rothIra: 7000, k401: 23000 },
  { year: 2025, rothIra: 7000, k401: 23500 },
  // IRS Notice 2025-67; future years retain the latest published limits.
  { year: 2026, rothIra: 7500, k401: 24500 },
];

export type PayFrequency = "weekly" | "biweekly" | "semimonthly" | "monthly";

const PAY_FREQUENCY_MULTIPLIER: Record<PayFrequency, number> = {
  weekly: 52,
  biweekly: 26,
  semimonthly: 24,
  monthly: 12,
};

const SS_RATE = 0.062;
const MEDICARE_RATE = 0.0145;
const SS_WAGE_BASE = 168_600;

interface Bracket {
  min: number;
  rate: number;
}

interface PayrollFederalTable {
  single: Bracket[];
  married_filing_jointly: Bracket[];
  stdDeductionSingle: number;
  stdDeductionMfj: number;
}

const PAYROLL_FEDERAL_TABLES: Record<number, PayrollFederalTable> = {
  2025: {
    single: [
      { min: 0, rate: 0.1 },
      { min: 11_925, rate: 0.12 },
      { min: 48_475, rate: 0.22 },
      { min: 103_350, rate: 0.24 },
      { min: 197_300, rate: 0.32 },
      { min: 250_525, rate: 0.35 },
      { min: 626_350, rate: 0.37 },
    ],
    married_filing_jointly: [
      { min: 0, rate: 0.1 },
      { min: 23_850, rate: 0.12 },
      { min: 96_950, rate: 0.22 },
      { min: 206_700, rate: 0.24 },
      { min: 394_600, rate: 0.32 },
      { min: 501_050, rate: 0.35 },
      { min: 751_600, rate: 0.37 },
    ],
    stdDeductionSingle: 15_000,
    stdDeductionMfj: 30_000,
  },
};

const STATE_DEFAULT_WITHHOLDING_RATES: Record<string, number> = {
  AK: 0,
  AL: 0.04,
  AR: 0.044,
  AZ: 0.025,
  CA: 0.06,
  CO: 0.044,
  CT: 0.05,
  DC: 0.06,
  DE: 0.052,
  FL: 0,
  GA: 0.05,
  HI: 0.06,
  IA: 0.038,
  ID: 0.058,
  IL: 0.0495,
  IN: 0.0305,
  KS: 0.0525,
  KY: 0.04,
  LA: 0.0425,
  MA: 0.05,
  MD: 0.0475,
  ME: 0.058,
  MI: 0.0425,
  MN: 0.068,
  MO: 0.048,
  MS: 0.047,
  MT: 0.047,
  NC: 0.0475,
  ND: 0.022,
  NE: 0.055,
  NH: 0,
  NJ: 0.055,
  NM: 0.049,
  NV: 0,
  NY: 0.0585,
  OH: 0.035,
  OK: 0.04,
  OR: 0.0875,
  PA: 0.0307,
  RI: 0.0475,
  SC: 0.064,
  SD: 0,
  TN: 0,
  TX: 0,
  UT: 0.0465,
  VA: 0.0575,
  VT: 0.066,
  WA: 0,
  WI: 0.053,
  WV: 0.0475,
  WY: 0,
};

export interface ContributionLimits {
  rothLimit: number;
  taxDeferredLimit: number;
}

export interface CappedEmployeeRetirementContributions {
  employeePretax: number;
  employeeRoth: number;
  annualLimit: number;
}

export interface StoredWageIncomeCalculationInput {
  year: number;
  monthlyGross: number;
  federalTax?: number;
  stateTax?: number;
  socialSecurityTax?: number;
  medicareTax?: number;
  healthInsurance?: number;
  dentalInsurance?: number;
  visionInsurance?: number;
  hsaContribution?: number;
  otherDeductions?: number;
  retirement401kPct?: number;
  roth401kPct?: number;
  employer401kMatchPct?: number;
}

export interface StoredWageIncomeCalculation {
  monthlyGross: number;
  annualGross: number;
  retirement401kPct: number;
  roth401kPct: number;
  employer401kMatchPct: number;
  retirement401kAmountMonthly: number;
  roth401kAmountMonthly: number;
  employer401kMatchAmountMonthly: number;
  retirement401kAmountAnnual: number;
  roth401kAmountAnnual: number;
  employer401kMatchAmountAnnual: number;
  totalDeductionsMonthly: number;
  totalDeductionsAnnual: number;
  monthlyTakeHome: number;
  annualTakeHome: number;
}

export interface PayrollDeductionDefaultsInput {
  annualGross: number;
  filingStatus?: "single" | "married_filing_jointly" | string | null;
  state?: string | null;
  payFrequency?: PayFrequency;
  year?: number;
}

type EstimatedPayrollTaxFields = Pick<
  StoredWageIncomeCalculationInput,
  "federalTax" | "stateTax" | "socialSecurityTax" | "medicareTax"
>;

export interface EstimatedPayrollDeductionDefaults {
  monthly: EstimatedPayrollTaxFields;
  perPaycheck: EstimatedPayrollTaxFields;
}

function toFiniteNumber(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value)) return 0;
  return value;
}

export function getContributionLimits(year: number): ContributionLimits {
  let entry: ContributionLimitEntry = CONTRIBUTION_LIMITS[0]!;
  for (const limit of CONTRIBUTION_LIMITS) {
    if (limit.year <= year) entry = limit;
    else break;
  }
  return { rothLimit: entry.rothIra, taxDeferredLimit: entry.k401 };
}

export function capEmployeeRetirementContributions(
  employeePretax: number,
  employeeRoth: number,
  year: number,
): CappedEmployeeRetirementContributions {
  const safePretax = Math.max(toFiniteNumber(employeePretax), 0);
  const safeRoth = Math.max(toFiniteNumber(employeeRoth), 0);
  const total = safePretax + safeRoth;
  const { taxDeferredLimit } = getContributionLimits(year);

  if (total <= taxDeferredLimit || total === 0) {
    return {
      employeePretax: safePretax,
      employeeRoth: safeRoth,
      annualLimit: taxDeferredLimit,
    };
  }

  const scale = taxDeferredLimit / total;
  return {
    employeePretax: safePretax * scale,
    employeeRoth: safeRoth * scale,
    annualLimit: taxDeferredLimit,
  };
}

function getPayrollFederalTable(year: number): PayrollFederalTable {
  const years = Object.keys(PAYROLL_FEDERAL_TABLES)
    .map(Number)
    .sort((a, b) => b - a);
  for (const tableYear of years) {
    if (year >= tableYear) return PAYROLL_FEDERAL_TABLES[tableYear]!;
  }
  return PAYROLL_FEDERAL_TABLES[2025]!;
}

function applyBrackets(taxableIncome: number, brackets: Bracket[]): number {
  if (taxableIncome <= 0) return 0;
  let tax = 0;
  for (let i = 0; i < brackets.length; i += 1) {
    const bracket = brackets[i]!;
    const next = brackets[i + 1];
    const upper = next?.min ?? Infinity;
    if (taxableIncome <= bracket.min) break;
    tax += (Math.min(taxableIncome, upper) - bracket.min) * bracket.rate;
  }
  return tax;
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

export function estimatePayrollDeductionDefaults(
  input: PayrollDeductionDefaultsInput,
): EstimatedPayrollDeductionDefaults {
  const annualGross = Math.max(toFiniteNumber(input.annualGross), 0);
  const payFrequency = input.payFrequency ?? "biweekly";
  const paycheckCount = PAY_FREQUENCY_MULTIPLIER[payFrequency] ?? 26;
  const filingStatus =
    input.filingStatus === "married_filing_jointly"
      ? "married_filing_jointly"
      : "single";
  const year = input.year ?? new Date().getFullYear();
  const federalTable = getPayrollFederalTable(year);
  const standardDeduction =
    filingStatus === "married_filing_jointly"
      ? federalTable.stdDeductionMfj
      : federalTable.stdDeductionSingle;
  const federalAnnual = applyBrackets(
    Math.max(0, annualGross - standardDeduction),
    federalTable[filingStatus],
  );
  const normalizedState = input.state?.trim().toUpperCase() ?? "";
  const stateRate =
    STATE_DEFAULT_WITHHOLDING_RATES[normalizedState] ??
    (normalizedState ? 0.045 : 0);
  const stateAnnual = annualGross * stateRate;
  const socialSecurityAnnual = Math.min(annualGross, SS_WAGE_BASE) * SS_RATE;
  const medicareAnnual = annualGross * MEDICARE_RATE;

  return {
    monthly: {
      federalTax: roundCents(federalAnnual / 12),
      stateTax: roundCents(stateAnnual / 12),
      socialSecurityTax: roundCents(socialSecurityAnnual / 12),
      medicareTax: roundCents(medicareAnnual / 12),
    },
    perPaycheck: {
      federalTax: roundCents(federalAnnual / paycheckCount),
      stateTax: roundCents(stateAnnual / paycheckCount),
      socialSecurityTax: roundCents(socialSecurityAnnual / paycheckCount),
      medicareTax: roundCents(medicareAnnual / paycheckCount),
    },
  };
}

export function calculateStoredWageIncome(
  input: StoredWageIncomeCalculationInput,
): StoredWageIncomeCalculation {
  const monthlyGross = Math.max(toFiniteNumber(input.monthlyGross), 0);
  const annualGross = monthlyGross * 12;

  const federalTax = Math.max(toFiniteNumber(input.federalTax), 0);
  const stateTax = Math.max(toFiniteNumber(input.stateTax), 0);
  const socialSecurityTax = Math.max(toFiniteNumber(input.socialSecurityTax), 0);
  const medicareTax = Math.max(toFiniteNumber(input.medicareTax), 0);
  const healthInsurance = Math.max(toFiniteNumber(input.healthInsurance), 0);
  const dentalInsurance = Math.max(toFiniteNumber(input.dentalInsurance), 0);
  const visionInsurance = Math.max(toFiniteNumber(input.visionInsurance), 0);
  const hsaContribution = Math.max(toFiniteNumber(input.hsaContribution), 0);
  const otherDeductions = Math.max(toFiniteNumber(input.otherDeductions), 0);

  const retirement401kPct = Math.max(toFiniteNumber(input.retirement401kPct), 0);
  const roth401kPct = Math.max(toFiniteNumber(input.roth401kPct), 0);
  const employer401kMatchPct = Math.max(toFiniteNumber(input.employer401kMatchPct), 0);

  const uncappedPretaxAnnual = (retirement401kPct / 100) * annualGross;
  const uncappedRothAnnual = (roth401kPct / 100) * annualGross;
  const cappedEmployee = capEmployeeRetirementContributions(
    uncappedPretaxAnnual,
    uncappedRothAnnual,
    input.year,
  );

  const retirement401kAmountAnnual = cappedEmployee.employeePretax;
  const roth401kAmountAnnual = cappedEmployee.employeeRoth;
  const employer401kMatchAmountAnnual = (employer401kMatchPct / 100) * annualGross;

  const retirement401kAmountMonthly = retirement401kAmountAnnual / 12;
  const roth401kAmountMonthly = roth401kAmountAnnual / 12;
  const employer401kMatchAmountMonthly = employer401kMatchAmountAnnual / 12;

  const totalDeductionsMonthly =
    federalTax +
    stateTax +
    socialSecurityTax +
    medicareTax +
    healthInsurance +
    dentalInsurance +
    visionInsurance +
    retirement401kAmountMonthly +
    roth401kAmountMonthly +
    hsaContribution +
    otherDeductions;
  const monthlyTakeHome = monthlyGross - totalDeductionsMonthly;

  return {
    monthlyGross,
    annualGross,
    retirement401kPct,
    roth401kPct,
    employer401kMatchPct,
    retirement401kAmountMonthly,
    roth401kAmountMonthly,
    employer401kMatchAmountMonthly,
    retirement401kAmountAnnual,
    roth401kAmountAnnual,
    employer401kMatchAmountAnnual,
    totalDeductionsMonthly,
    totalDeductionsAnnual: totalDeductionsMonthly * 12,
    monthlyTakeHome,
    annualTakeHome: monthlyTakeHome * 12,
  };
}
